/**
 * Scene content tools for AI automation: text + hotspots.
 * Uses the same create paths as the Grid toolbar (handleAddText / handleAddHotSpot).
 */

import { handleAddText } from '../../components/gridpagefunction/Text';
import { handleAddHotSpot } from '../../components/gridpagefunction/HotSpot';
import { TextInitialData, HotSpotInitialData } from '../constant';
import {
  normalizeColor,
  pickCreateProps,
  sanitizeTextContent,
} from './propertyHelpers';

/**
 * Build the scene context object expected by Text / HotSpot helpers.
 */
export function buildSceneContext(scene, transformControls = null) {
  const fromWindow = window.gridPageSceneObjects || {};
  return {
    scene: scene || fromWindow.scene,
    transformControls: transformControls || fromWindow.transformControls,
    camera: fromWindow.currentCamera || fromWindow.camera,
    renderer: fromWindow.renderer,
    currentCamera: fromWindow.currentCamera,
    perspectiveCamera: fromWindow.perspectiveCamera,
    orthographicCamera: fromWindow.orthographicCamera,
    controlsOrbit: fromWindow.controlsOrbit,
    update: fromWindow.update,
  };
}

/**
 * Add 3D/2D text to the scene.
 * @param {object} parameters - { content, color, fontSize, textType, position, ... }
 */
export async function executeAddText(parameters, scene, transformControls) {
  const raw = parameters && typeof parameters === 'object' ? parameters : {};
  const props = pickCreateProps(raw);

  const content = sanitizeTextContent(
    raw.content ?? raw.text ?? raw.message ?? props.content ?? 'Spatial Grid'
  );

  const color =
    normalizeColor(raw.color ?? props.color) || TextInitialData.color;

  let fontSize = Number(raw.fontSize ?? props.fontSize ?? props.size);
  if (!Number.isFinite(fontSize) || fontSize <= 0) {
    fontSize = TextInitialData.fontSize;
  }
  fontSize = Math.min(Math.max(fontSize, 8), 400);

  const textTypeRaw = String(
    raw.textType ?? raw.type ?? props.textType ?? '3D'
  ).toUpperCase();
  const textType = textTypeRaw === '2D' ? '2D' : '3D';

  const context = buildSceneContext(scene, transformControls);
  if (!context.scene) {
    return { success: false, message: 'Scene is not ready for text' };
  }

  const textData = {
    ...TextInitialData,
    content,
    color,
    fontSize,
    textType,
    fontFamily: raw.fontFamily || TextInitialData.fontFamily,
    fontWeight: raw.fontWeight || TextInitialData.fontWeight,
    horizontal: raw.horizontal || TextInitialData.horizontal,
    vertical: raw.vertical || TextInitialData.vertical,
    extrusion:
      raw.extrusion != null
        ? Number(raw.extrusion)
        : TextInitialData.extrusion,
    metalness:
      raw.metalness != null
        ? Number(raw.metalness)
        : TextInitialData.metalness,
    roughness:
      raw.roughness != null
        ? Number(raw.roughness)
        : TextInitialData.roughness,
  };

  if (props.position || raw.position) {
    textData.position = props.position || raw.position;
  }
  if (props.rotation || raw.rotation) {
    textData.rotation = props.rotation || raw.rotation;
  }
  if (props.scale || raw.scale) {
    textData.scale = props.scale || raw.scale;
  }
  if (raw.file_name || raw.name) {
    textData.file_name = String(raw.file_name || raw.name).slice(0, 120);
  }

  const mesh = await handleAddText(context, textData, 'add');
  if (!mesh) {
    return { success: false, message: 'Failed to add text to scene' };
  }

  return {
    success: true,
    message: `Added ${textType} text "${content}"`,
    id: mesh.name || mesh._id,
    content,
  };
}

/**
 * Add a hotspot with optional button/message labels and colors.
 */
export async function executeAddHotspot(parameters, scene, transformControls) {
  const raw = parameters && typeof parameters === 'object' ? parameters : {};
  const props = pickCreateProps(raw);

  const context = buildSceneContext(scene, transformControls);
  if (!context.scene) {
    return { success: false, message: 'Scene is not ready for hotspot' };
  }

  const buttonText = sanitizeTextContent(
    raw.buttonText ?? raw.label ?? raw.content ?? HotSpotInitialData.buttonText,
    HotSpotInitialData.buttonText
  );
  const messageText = sanitizeTextContent(
    raw.messageText ??
      raw.message ??
      raw.description ??
      HotSpotInitialData.messageText,
    HotSpotInitialData.messageText
  );

  const hotspotData = {
    ...HotSpotInitialData,
    buttonText,
    messageText,
    buttonColor:
      normalizeColor(raw.buttonColor ?? raw.color ?? props.color) ||
      HotSpotInitialData.buttonColor,
    buttonTextColor:
      normalizeColor(raw.buttonTextColor) ||
      HotSpotInitialData.buttonTextColor,
    messageTextColor:
      normalizeColor(raw.messageTextColor) ||
      HotSpotInitialData.messageTextColor,
    messageBgColor:
      normalizeColor(raw.messageBgColor ?? raw.backgroundColor) ||
      HotSpotInitialData.messageBgColor,
    buttonShape: raw.buttonShape === 'Rectangle' ? 'Rectangle' : 'Circle',
    buttonOpacity:
      raw.buttonOpacity != null
        ? Number(raw.buttonOpacity)
        : raw.opacity != null
          ? Number(raw.opacity)
          : HotSpotInitialData.buttonOpacity,
    messageOpacity:
      raw.messageOpacity != null
        ? Number(raw.messageOpacity)
        : HotSpotInitialData.messageOpacity,
  };

  const group = handleAddHotSpot(
    context,
    hotspotData,
    null,
    null,
    null,
    true,
    raw.name || raw.file_name || null
  );

  if (!group) {
    return { success: false, message: 'Failed to add hotspot' };
  }

  if (props.position || raw.position) {
    const p = props.position || raw.position;
    group.position.set(
      Number(p.x) || 0,
      Number(p.y) || 0,
      Number(p.z) || 0
    );
  }

  return {
    success: true,
    message: `Added hotspot "${buttonText}"`,
    id: group.name || group._id,
  };
}
